import { forwardRef, useImperativeHandle, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface DeleteConfirmDialogProps {
  onRemoveItem: (id: string) => void;
}

export interface DeleteConfirmDialogRef {
  showDeleteConfirm: (itemId: string) => void;
}

const DeleteConfirmDialog = forwardRef<DeleteConfirmDialogRef, DeleteConfirmDialogProps>(({ onRemoveItem }, ref) => {
  const [open, setOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<string | null>(null);

  useImperativeHandle(ref, () => ({
    showDeleteConfirm: (itemId: string) => {
      setItemToDelete(itemId);
      setOpen(true);
    },
  }));

  const handleConfirm = () => {
    if (itemToDelete) {
      onRemoveItem(itemToDelete);
      setItemToDelete(null);
      setOpen(false);
    }
  };

  const handleCancel = () => {
    setItemToDelete(null);
    setOpen(false);
  };

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) {
      setItemToDelete(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="sr-only">메뉴 삭제</DialogTitle>
          <DialogDescription>선택하신 메뉴를 삭제하시겠습니까?</DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex flex-row gap-2 sm:gap-2">
          <Button variant="outline" onClick={handleCancel} className="flex-1">
            취소
          </Button>
          <Button variant="destructive" onClick={handleConfirm} className="flex-1 bg-[#3D8AFF] text-white">
            삭제
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});

DeleteConfirmDialog.displayName = 'DeleteConfirmDialog';

export default DeleteConfirmDialog;
